import { useCallback, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S0 — Login (Splash / Login screen)

   Branded fullscreen layout.
   Single-field-at-a-time: email first, then password.
   ========================================================================== */

type Step = 'email' | 'password';

export function Login() {
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------

  const handleEmailSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setError('');
      if (!email.trim()) {
        setError('Inserisci la tua email');
        return;
      }
      setStep('password');
    },
    [email],
  );

  const handlePasswordSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');
      if (!password) {
        setError('Inserisci la password');
        return;
      }

      setLoading(true);
      try {
        await login({ email: email.trim(), password });
        toast.success('Accesso effettuato');
        navigate('/', { replace: true });
      } catch (err) {
        if (err instanceof ApiError) {
          setError(
            err.status === 401
              ? 'Credenziali non valide'
              : err.message,
          );
        } else {
          setError('Errore di connessione. Riprova.');
        }
      } finally {
        setLoading(false);
      }
    },
    [email, password, login, toast, navigate],
  );

  const handleBack = useCallback(() => {
    setStep('email');
    setPassword('');
    setError('');
  }, []);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div
      className={cn(
        'flex min-h-dvh flex-col items-center justify-center',
        'px-6 py-12',
        /* Warm gradient background */
        'bg-gradient-to-b from-surface-base via-surface-base to-[#F2EDE8]',
      )}
    >
      {/* Subtle decorative circles */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-accent-primary/[0.04]" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-accent-secondary/[0.05]" />
      </div>

      {/* Content card */}
      <div className="relative z-10 w-full max-w-sm">
        {/* Brand header */}
        <div className="mb-10 text-center">
          {/* Logo placeholder — terracotta circle mark */}
          <div
            className={cn(
              'mx-auto mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-[var(--radius-xl)] bg-accent-primary shadow-md',
            )}
          >
            <span className="text-2xl font-bold text-white font-[family-name:var(--font-heading)]">
              B
            </span>
          </div>

          <h1 className="font-[family-name:var(--font-heading)] text-3xl font-extrabold text-text-primary tracking-tight">
            Beauty Checkup
          </h1>
          <p className="mt-2 text-base text-text-secondary">
            Accedi al tuo account
          </p>
        </div>

        {/* ---- Email step ---- */}
        {step === 'email' && (
          <form onSubmit={handleEmailSubmit} className="fade-in space-y-5">
            <Input
              label="Email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              placeholder="nome@centro.it"
              icon={<Mail strokeWidth={1.5} />}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              error={error || undefined}
            />

            <Button
              type="submit"
              size="lg"
              fullWidth
              rightIcon={<ArrowRight strokeWidth={2} />}
            >
              Continua
            </Button>
          </form>
        )}

        {/* ---- Password step ---- */}
        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="fade-in space-y-5">
            {/* Hidden username field per password manager / autofill
                (a11y: i form di password devono avere un campo username
                associato anche se invisibile) */}
            <input
              type="email"
              name="username"
              autoComplete="username"
              value={email}
              readOnly
              hidden
              tabIndex={-1}
            />
            {/* Email reminder */}
            <button
              type="button"
              onClick={handleBack}
              className={cn(
                'flex items-center gap-2 text-sm text-text-secondary',
                'hover:text-accent-primary transition-colors cursor-pointer',
                'group',
              )}
            >
              <Mail className="h-4 w-4" strokeWidth={1.5} />
              <span className="truncate">{email}</span>
              <span className="text-text-muted group-hover:text-accent-primary">&mdash; modifica</span>
            </button>

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                autoFocus
                placeholder="La tua password"
                icon={<Lock strokeWidth={1.5} />}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                error={error || undefined}
              />
              {/* Toggle visibility */}
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className={cn(
                  'absolute right-4 top-[38px]',
                  'text-text-muted hover:text-text-primary',
                  'transition-colors cursor-pointer',
                )}
                aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" strokeWidth={1.5} />
                ) : (
                  <Eye className="h-5 w-5" strokeWidth={1.5} />
                )}
              </button>
            </div>

            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={loading}
              rightIcon={!loading ? <ArrowRight strokeWidth={2} /> : undefined}
            >
              Accedi
            </Button>
          </form>
        )}
      </div>

      {/* Footer */}
      <p className="relative z-10 mt-12 text-xs text-text-muted">
        &copy; {new Date().getFullYear()} Beauty Checkup
      </p>
    </div>
  );
}
